import { InputNumberLocalized } from '@components'

import {
  Checkbox,
  Flex,
  FormItemsGroup,
  Icon,
  Input,
  NavListItemWithBlockDescription,
  Select,
  Space,
  Spacing,
  Text,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { SiretPendingAssignmentCheckbox } from '@features/invoicingSettings/elements/SiretPendingAssignment'
import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'

import { useResolveTaxAndRegistrationNoLabel } from '../../../../../../../../hooks/useResolveTaxAndRegistrationNoLabel'
import { CompanySearchField } from '../../../../../../../companies/elements/CompanySearchField'
import { isFrenchCompany } from '../../../../../../../companies/utils/isFrenchCompany'
import { mapCompanyToCompanyDetails } from '../../../../../../../companies/utils/mapCompanyToCompanyDetails'
import { shouldAllowVatNumberWhenExempt } from '../../../../../../../invoices/utils/shouldAllowVatNumberWhenExempt'
import { countryIdToRegistrationNoPlaceholderTKey } from '../../../../../../constants/countryIdToRegistrationNoPlaceholderTKey'
import { countryIdToVatNoPlaceholderTKey } from '../../../../../../constants/countryIdToVatNoPlaceholderTKey'
import { MAX_COMPANY_NAME_LENGTH } from '../../../../../../constants/maxCompanyNameLength'
import { FrenchLegalStatus } from '../../../../../../enums/frenchLegalStatus'
import { useShouldShowSiretPendingAssignmentCheckbox } from '../../../../../../hooks/useShouldShowSiretPendingAssignmentCheckbox'
import { LegalStatusDisplayItem } from '../../../../../../types/legalStatusDisplayItem'
import { getFrenchLegalStatusOptions } from '../../../../../../utils/getFrenchLegalStatusOptions'
import { useCompanyInformationForm } from '../../../../contexts/companyInformationFormContext'

export const CompanyDetails = (): ReactElement => {
  const { t } = useTranslation()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { vatNoLabel, registrationNoLabel } = useResolveTaxAndRegistrationNoLabel()
  const shouldShowSiretPendingCheckbox = useShouldShowSiretPendingAssignmentCheckbox()
  const { companyInformationForm, countryId } = useCompanyInformationForm()

  const { getValues, FormItem, trigger, watch, reset, setValue } = companyInformationForm

  const [companyName, isSiretPendingAssignmentValue, legalStatusOption, registrationNoValue, isVatExempted] = watch([
    'companyName',
    'isSiretPendingAssignment',
    'legalStatusOption',
    'registrationNo',
    'isVatExempted',
  ])
  const allowVatNumberWhenExempt = shouldAllowVatNumberWhenExempt(countryId)

  const defaultRegistrationNoPlaceholder = countryId ? t(countryIdToRegistrationNoPlaceholderTKey[countryId]) : ''
  const registrationNoPlaceholder = isSiretPendingAssignmentValue
    ? t('external_invoices.missing_customer_compliance_details.modal.form.field.siret_number.placeholder')
    : defaultRegistrationNoPlaceholder
  const vatNoPlaceholder = countryId ? t(countryIdToVatNoPlaceholderTKey[countryId]) : ''
  const isVatNumberDisabled = isVatExempted && !allowVatNumberWhenExempt

  const handleSelectCompany = useCallback(
    (company: Partial<Company> | Partial<FrenchCompany>) => {
      const companyDetails = mapCompanyToCompanyDetails(company, countryId)
      const currentVatNo = getValues('vatNo')
      const isVatNoAutoFilled = getValues('autoCompletedFields')?.vatNo
      const isFrench = isFrenchCompany(company)
      const shouldUpdateVatNo = isFrench && !isVatExempted && (!currentVatNo || isVatNoAutoFilled)

      reset({
        ...getValues(),
        companyAddress: companyDetails.companyAddress,
        companyCity: companyDetails.companyCity,
        companyZipCode: companyDetails.companyZipCode,
        registrationNo: companyDetails.registrationNo,
        ...(isFrenchComplianceMode && {
          isSiretPendingAssignment: companyDetails.isSiretPendingAssignment,
          legalStatusCustom: companyDetails.legalStatusCustom,
          legalStatusOption: companyDetails.legalStatusOption,
        }),
        ...(shouldUpdateVatNo && { vatNo: companyDetails.vatNo || '' }),
      })

      // Ensure the reset has been fully applied before setting `autoCompletedFields`, so the highlight state is not wiped out.
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', {
          companyAddress: !!companyDetails.companyAddress,
          companyCity: !!companyDetails.companyCity,
          companyZipCode: !!companyDetails.companyZipCode,
          registrationNo: !!companyDetails.registrationNo,
          ...(isFrenchComplianceMode && {
            legalStatusCustom: !!companyDetails.legalStatusCustom,
            legalStatusOption: !!companyDetails.legalStatusOption,
          }),
          ...(shouldUpdateVatNo && { vatNo: !!companyDetails.vatNo }),
        })
      })
    },
    [countryId, getValues, isFrenchComplianceMode, isVatExempted, reset, setValue],
  )

  return (
    <FormItemsGroup rowSpacing={Spacing.S16}>
      <FormItem
        name="companyName"
        label={
          <Flex justifyContent="space-between">
            <Text colorVariant="secondary">{t('external_invoices.company_information_modal.company_name')}</Text>
            <Text colorVariant="secondary">{`${companyName.length}/${MAX_COMPANY_NAME_LENGTH}`}</Text>
          </Flex>
        }
        render={({ field }) => {
          return (
            <CompanySearchField
              {...field}
              maxLength={MAX_COMPANY_NAME_LENGTH}
              onSelectCompany={handleSelectCompany}
              size="md"
            />
          )
        }}
      />

      {countryId === 'FR' && (
        <Flex flexDirection="column" gap={Spacing.S8}>
          <FormItemsGroup itemsInRow={2} columnSpacing={Spacing.S12}>
            <FormItem
              name="legalStatusOption"
              label={t('settings.organization.organization_info.basic_info.legal_status.label')}
              render={({ field: { value, onChange, ...props } }) => (
                <Select<LegalStatusDisplayItem>
                  {...props}
                  items={getFrenchLegalStatusOptions(t)}
                  dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
                  dropdownSize="xxxl"
                  dropdownMaxHeight="fullScreen"
                  onSelect={(id?: string) => onChange(id)}
                  placement="bottom-start"
                  selectedId={value}
                  withSearch={false}
                />
              )}
            />

            <FormItem
              name="sharedCapital"
              label={t('settings.organization.organization_info.basic_info.shared_capital.label')}
              render={({ field }) => (
                <InputNumberLocalized
                  {...field}
                  alignment="left"
                  min={0}
                  placeholder="0.00"
                  suffix={<Icon icon="euro" />}
                />
              )}
            />
          </FormItemsGroup>
          {legalStatusOption === FrenchLegalStatus.Other && (
            <FormItem
              name="legalStatusCustom"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('settings.organization.organization_info.basic_info.legal_status_custom.placeholder')}
                />
              )}
            />
          )}
        </Flex>
      )}

      <FormItemsGroup rowSpacing={Spacing.S16}>
        <FormItem
          name="registrationNo"
          label={registrationNoLabel}
          render={({ field }) => (
            <Input
              {...field}
              placeholder={registrationNoPlaceholder}
              disabled={isSiretPendingAssignmentValue}
              prefix={isSiretPendingAssignmentValue && <Icon icon="info" />}
            />
          )}
        />

        {shouldShowSiretPendingCheckbox && (
          <FormItem
            name="isSiretPendingAssignment"
            render={({ field: { value, ...field } }) => (
              <SiretPendingAssignmentCheckbox {...field} checked={value} registrationNoValue={registrationNoValue} />
            )}
          />
        )}
        {countryId !== 'DK' ? (
          <>
            <FormItem
              name="vatNo"
              label={vatNoLabel}
              render={({ field: { value, ...field } }) => (
                <Input {...field} disabled={isVatNumberDisabled} placeholder={vatNoPlaceholder} value={value} />
              )}
            />
            <FormItem
              name="isVatExempted"
              render={({ field: { onChange, value, ...rest } }) => (
                <Checkbox
                  {...rest}
                  checked={value}
                  onChange={(event) => {
                    onChange(event)
                    if (!allowVatNumberWhenExempt) {
                      setValue('vatNo', '')
                      trigger('vatNo')
                    }
                  }}
                >
                  {t('settings.organization.organization_info.vat_exempted')}
                </Checkbox>
              )}
            />
            <Space size="s2" />
          </>
        ) : null}
      </FormItemsGroup>
    </FormItemsGroup>
  )
}
